import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar.svg';
import DJPalDateRangeModal from './DJPalDateRangeModal';
import MobileTypedSearchOverlay from '../MobileTypedSearchOverlay';
import { MIN_SEARCH_CHARS } from '../../utils/searchConfig';
import CoverArt from '../CoverArt';

const MOBILE_MEDIA_QUERY = '(max-width: 768px)';
const matchMobile = () => typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia(MOBILE_MEDIA_QUERY).matches;

const DJPalSearchBar = ({
    placeholder,
    value,
    onChange,
    onSearch,
    onClear,
    onDateRangeClick,
    onDateRangeClose,
    onDateApply,
    showDateRange,
    dateRange,
    disabled,
    dateRangeBtnRef,
    onSuggestionSelect,
    fetchSuggestions,
    // Recent-searches dropdown (optional): shown when input is focused and
    // the query is empty. Autocomplete takes precedence as soon as the user types.
    recentEntries,
    onRecentSelect,
    onRecentDismiss,
    onRecentClear,
    recentLabel,
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(matchMobile);
    const [mobileOverlayOpen, setMobileOverlayOpen] = useState(false);
    const wrapperRef = useRef(null);

    // Track viewport so rotation between portrait/landscape doesn't strand
    // the user in the wrong mode.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
        const handler = (e) => setIsMobile(e.matches);
        if (mql.addEventListener) mql.addEventListener('change', handler);
        else if (mql.addListener) mql.addListener(handler);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', handler);
            else if (mql.removeListener) mql.removeListener(handler);
        };
    }, []);
    const datePopoverRef = useRef(null);
    const debounceRef = useRef(null);
    const loadingTimerRef = useRef(null);
    const keyboardNavRef = useRef(false);
    // Bumped when the user commits (Enter/select) or clears — any in-flight
    // fetch older than the current seq is discarded so a late resolve can't
    // reopen the dropdown over the already-committed search.
    const fetchSeqRef = useRef(0);

    const hasRecents = Array.isArray(recentEntries) && recentEntries.length > 0;
    const showRecents = open && hasRecents && (value || '').trim().length < MIN_SEARCH_CHARS && suggestions.length === 0;

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Close date popover on outside click
    useEffect(() => {
        if (!showDateRange || !onDateRangeClose) return undefined;
        const onDown = (e) => {
            if (datePopoverRef.current && !datePopoverRef.current.contains(e.target)) {
                onDateRangeClose();
            }
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [showDateRange, onDateRangeClose]);

    const doFetch = useCallback(async (q) => {
        if (!fetchSuggestions || q.length < MIN_SEARCH_CHARS) { setSuggestions([]); return; }
        const seq = ++fetchSeqRef.current;
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = setTimeout(() => {
            if (seq === fetchSeqRef.current) setLoading(true);
        }, 200);
        try {
            const results = await fetchSuggestions(q);
            if (seq !== fetchSeqRef.current) return;
            setSuggestions(results || []);
            setSelectedIdx(-1);
            keyboardNavRef.current = false;
            setOpen((results || []).length > 0);
        } catch {
            if (seq === fetchSeqRef.current) setSuggestions([]);
        } finally {
            clearTimeout(loadingTimerRef.current);
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    }, [fetchSuggestions]);

    const handleChange = (val) => {
        onChange(val);
        clearTimeout(debounceRef.current);
        // Bump the fetch seq on every change so any in-flight fetch from a
        // longer prior query is invalidated. Without this, deleting "paul"
        // back to "" left a pending fetch for "paul" that resolved later
        // and overwrote the just-cleared suggestion list.
        fetchSeqRef.current++;
        if (!fetchSuggestions || val.trim().length < MIN_SEARCH_CHARS) {
            setSuggestions([]);
            // Cancel any pending loading-spinner reveal and force-clear it.
            // doFetch's finally block guards setLoading(false) behind the
            // seq match (so a stale fetch can't toggle UI), so once we've
            // bumped the seq the in-flight fetch can no longer clear the
            // spinner — we have to clear it here.
            clearTimeout(loadingTimerRef.current);
            setLoading(false);
            // Switching back to the recent-searches view: drop the
            // arrow-key selection so the user starts from the top instead
            // of inheriting a stale suggestion index that points into the
            // (now-empty) suggestion list.
            setSelectedIdx(-1);
            keyboardNavRef.current = false;
            // Keep dropdown open so recent-searches view is visible when the
            // input is empty and we have history; otherwise close it.
            setOpen(hasRecents);
            return;
        }
        debounceRef.current = setTimeout(() => doFetch(val.trim()), 300);
    };

    const handleSelect = (suggestion) => {
        clearTimeout(debounceRef.current);
        fetchSeqRef.current++;
        setOpen(false);
        setSuggestions([]);
        if (onSuggestionSelect) {
            onSuggestionSelect(suggestion);
        }
    };

    // Scroll-vs-tap guard for the dropdown. Three layers because no single
    // signal is reliable on iOS Safari:
    //   1. Parent onScroll → set a flag for 400ms. Catches any gesture that
    //      actually moves the list (most "I was trying to scroll" cases).
    //   2. Per-item onTouchStart records the touch start position; onClick
    //      compares it to the synthesized click coords. iOS positions the
    //      synthesized mousedown/click at the touch END, so this delta
    //      captures finger drift during brief flicks that didn't scroll
    //      far enough to fire onScroll (boundary flicks, tiny movements).
    //   3. Standard onClick handles desktop mouse (touchStartRef stays null).
    const justScrolledRef = useRef(false);
    const scrollResetTimer = useRef(null);
    const touchStartRef = useRef(null);
    const handleDropdownScroll = () => {
        justScrolledRef.current = true;
        clearTimeout(scrollResetTimer.current);
        scrollResetTimer.current = setTimeout(() => {
            justScrolledRef.current = false;
        }, 400);
    };
    const itemTapHandlers = (action) => ({
        onTouchStart: (e) => {
            if (e.target.closest('.djpal-recent-dismiss')) {
                touchStartRef.current = null;
                return;
            }
            const t = e.touches[0];
            touchStartRef.current = { x: t.clientX, y: t.clientY };
        },
        onClick: (e) => {
            if (justScrolledRef.current) return;
            const start = touchStartRef.current;
            touchStartRef.current = null;
            if (start) {
                const dx = e.clientX - start.x;
                const dy = e.clientY - start.y;
                if (dx * dx + dy * dy > 144) return; // > 12px = scrolled
            }
            action();
        },
    });

    const handleClear = () => {
        clearTimeout(debounceRef.current);
        clearTimeout(loadingTimerRef.current);
        fetchSeqRef.current++;
        setLoading(false);
        setSuggestions([]);
        setOpen(false);
        onChange('');
        if (onClear) onClear();
    };

    const handleRecentSelect = (entry) => {
        clearTimeout(debounceRef.current);
        fetchSeqRef.current++;
        setOpen(false);
        setSuggestions([]);
        if (onRecentSelect) onRecentSelect(entry);
    };

    const handleRecentDismiss = (e, id) => {
        e.stopPropagation();
        if (onRecentDismiss) onRecentDismiss(id);
    };

    const handleRecentClear = () => {
        setOpen(false);
        if (onRecentClear) onRecentClear();
    };

    const handleKeyDown = (e) => {
        if (open && suggestions.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                keyboardNavRef.current = true;
                setSelectedIdx(prev => Math.min(prev + 1, suggestions.length - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                keyboardNavRef.current = true;
                setSelectedIdx(prev => Math.max(prev - 1, -1));
                return;
            }
            if (e.key === 'Enter' && selectedIdx >= 0 && keyboardNavRef.current) {
                e.preventDefault();
                handleSelect(suggestions[selectedIdx]);
                return;
            }
            if (e.key === 'Escape') {
                setOpen(false);
                return;
            }
        }
        if (showRecents) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                keyboardNavRef.current = true;
                setSelectedIdx(prev => Math.min(prev + 1, recentEntries.length - 1));
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                keyboardNavRef.current = true;
                setSelectedIdx(prev => Math.max(prev - 1, -1));
                return;
            }
            if (e.key === 'Enter' && selectedIdx >= 0 && keyboardNavRef.current && recentEntries[selectedIdx]) {
                e.preventDefault();
                handleRecentSelect(recentEntries[selectedIdx]);
                return;
            }
            if (e.key === 'Escape') {
                setOpen(false);
                return;
            }
        }
        if (e.key === 'Enter' && !disabled) {
            clearTimeout(debounceRef.current);
            clearTimeout(loadingTimerRef.current);
            fetchSeqRef.current++;
            setLoading(false);
            setSuggestions([]);
            setOpen(false);
            onSearch();
        }
    };

    return (
        <div className="djpal-search-bar" ref={wrapperRef}>
            <div className="search-input-wrapper">
                <SearchIcon className="search-icon search-icon-leading" onClick={() => !disabled && onSearch()} />
                <input
                    // type="search" + inputMode + enterKeyHint give iOS the
                    // proper "Search" return key and stop it from offering
                    // the autofill keychain / credit-card chip.
                    //
                    // iOS heuristic extra: autocomplete="off" alone is NOT
                    // always enough because WebKit ignores it for fields it
                    // thinks are username-y. Setting autoCapitalize + no
                    // meaningful `name` + `aria-autocomplete="list"` nudges
                    // it off the password-manager path.
                    type="search"
                    inputMode="search"
                    enterKeyHint="search"
                    placeholder={placeholder || 'Search...'}
                    value={value}
                    onChange={(e) => handleChange(e.target.value)}
                    onFocus={(e) => {
                        if (isMobile) {
                            // Don't let iOS open the keyboard for this
                            // inline input on mobile — the overlay's own
                            // input handles typing.
                            e.target.blur();
                            setMobileOverlayOpen(true);
                            return;
                        }
                        if (suggestions.length > 0 || hasRecents) setOpen(true);
                    }}
                    onClick={() => { if (isMobile) setMobileOverlayOpen(true); }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-autocomplete="list"
                    aria-label="Search tracks"
                    readOnly={isMobile}
                />
                {loading && (
                    <div style={{ position: 'absolute', right: 38, width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.15)', borderTopColor: 'var(--color-accent)', animation: 'spin 0.6s linear infinite', pointerEvents: 'none' }} />
                )}
                {!loading && value && !disabled && (
                    <button
                        type="button"
                        className="search-clear-btn"
                        onClick={handleClear}
                        aria-label="Clear search"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                )}

                {/* Autocomplete dropdown — anchored to the input bar.
                    onMouseDown preventDefault on the container stops the input from
                    losing focus on tap, which on iOS was tearing down the dropdown
                    before the onClick landed (classic "first tap does nothing"). */}
                {open && suggestions.length > 0 && (
                    <div
                        className="djpal-autocomplete-dropdown"
                        onMouseDown={(e) => e.preventDefault()}
                        onScroll={handleDropdownScroll}
                    >
                        {suggestions.map((s, i) => {
                            const primaryText = s.name || s.title || '';
                            return (
                                <button
                                    key={primaryText || i}
                                    type="button"
                                    className={`djpal-autocomplete-item ${i === selectedIdx ? 'selected' : ''}`}
                                    {...itemTapHandlers(() => handleSelect(s))}
                                    onMouseEnter={() => setSelectedIdx(i)}
                                >
                                    {s.artworkUrl ? (
                                        <CoverArt src={s.artworkUrl} size={32} eager className="autocomplete-artwork" />
                                    ) : (
                                        <div className="autocomplete-artwork autocomplete-artwork-placeholder">
                                            {(primaryText || '?').charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="autocomplete-info">
                                        <span className="autocomplete-name">{primaryText}</span>
                                        {s.subtitle && <span className="autocomplete-subtitle">{s.subtitle}</span>}
                                    </div>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.2, flexShrink: 0 }}>
                                        <path d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Recent-searches dropdown (only when input is empty and no autocomplete results) */}
                {showRecents && (
                    <div
                        className="djpal-autocomplete-dropdown djpal-recent-dropdown"
                        onMouseDown={(e) => e.preventDefault()}
                        onScroll={handleDropdownScroll}
                    >
                        <div className="djpal-recent-header">{recentLabel || 'Recent searches'}</div>
                        {recentEntries.map((entry, i) => {
                            const primary = entry.entityName || '';
                            const secondary = entry.entitySubtitle || null;
                            const fallback = (primary || '?').charAt(0).toUpperCase();
                            const artRadius = entry.entityType === 'artist' ? '50%' : 6;
                            const selected = i === selectedIdx;
                            return (
                                <button
                                    type="button"
                                    key={entry.id}
                                    className={`djpal-autocomplete-item djpal-recent-row ${selected ? 'selected' : ''}`}
                                    onMouseEnter={() => setSelectedIdx(i)}
                                    {...itemTapHandlers(() => handleRecentSelect(entry))}
                                >
                                    {entry.entityArtwork ? (
                                        <CoverArt src={entry.entityArtwork} size={32} eager className="autocomplete-artwork" style={{ borderRadius: artRadius }} />
                                    ) : (
                                        <div className="autocomplete-artwork autocomplete-artwork-placeholder" style={{ borderRadius: artRadius }}>
                                            {fallback}
                                        </div>
                                    )}
                                    <div className="autocomplete-info">
                                        <span className="autocomplete-name">{primary}</span>
                                        {secondary && <span className="autocomplete-subtitle">{secondary}</span>}
                                    </div>
                                    {onRecentDismiss && (
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            className="djpal-recent-dismiss"
                                            onClick={(e) => handleRecentDismiss(e, entry.id)}
                                            aria-label="Remove from recent searches"
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {onRecentClear && (
                            <button
                                type="button"
                                className="djpal-recent-clear"
                                onClick={handleRecentClear}
                            >
                                Clear recent searches
                            </button>
                        )}
                    </div>
                )}
            </div>
            {onDateRangeClick && (
                <div className="search-bar-below">
                    <div className="artist-header-date-range" ref={datePopoverRef}>
                        <button
                            ref={dateRangeBtnRef}
                            className={`date-range-btn ${dateRange ? 'active' : ''}`}
                            onClick={onDateRangeClick}
                        >
                            <CalendarIcon className="icon" />
                            {dateRange ? `${dateRange.from || '...'} - ${dateRange.to || '...'}` : 'Date Range'}
                        </button>
                        {showDateRange && onDateApply && (
                            <div className="artist-header-date-popover">
                                <DJPalDateRangeModal
                                    dateRange={dateRange}
                                    onApply={onDateApply}
                                    onClose={onDateRangeClose || (() => {})}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <MobileTypedSearchOverlay
                open={isMobile && mobileOverlayOpen}
                onClose={() => setMobileOverlayOpen(false)}
                placeholder={placeholder || 'Search'}
                fetchSuggestions={fetchSuggestions}
                onSuggestionSelect={onSuggestionSelect}
                recentEntries={recentEntries}
                recentLabel={recentLabel}
                onRecentSelect={onRecentSelect}
                onRecentDismiss={onRecentDismiss}
                onRecentClear={onRecentClear}
            />
        </div>
    );
};

export default DJPalSearchBar;
